import { useCallback } from "react";
import { useFormContext } from "@jsq/ds/forms";
import { hasBankInfo } from "../common/utils";
import { defaultBankAddress } from "../Components/BankValidators/constants";
import { type BankAddress } from "../Components/BankValidators/types";
import { type HandleValidateParams } from "./use-bank-validation";
import { useCachedCountryNameMap } from "./use-cached-country-name-map";

type UseGenericBankValidationParams = {
  nameField: string;
  addressField?: string;
  validatedCountryField?: string;
};

type UseGenericBankValidationReturn = {
  handleClear: () => void;
  handleValidateCompleted: (params: HandleValidateParams) => void;
};

export const useGenericBankValidation = <T extends Record<string, unknown>>({
  nameField,
  addressField,
  validatedCountryField,
}: UseGenericBankValidationParams): UseGenericBankValidationReturn => {
  const { values, setFieldValue } = useFormContext<T>();
  const countryNameMap = useCachedCountryNameMap();

  const handleClear = useCallback(() => {
    if (validatedCountryField) {
      void setFieldValue(validatedCountryField, "", false);
    }
    if (addressField) {
      void setFieldValue(addressField, defaultBankAddress, false);
    }
    // Note: routing number fields are intentionally not cleared here
    // to preserve the user's input while clearing validation results
    void setFieldValue(nameField, "", false);
  }, [setFieldValue, validatedCountryField, nameField, addressField]);

  const handleValidateCompleted = useCallback(
    ({ setShowBankValidatorError, bankName, bankAddress, isValid }: HandleValidateParams) => {
      const bankAddressWithDefaults: BankAddress = {
        country: bankAddress?.country || defaultBankAddress.country,
        line1: bankAddress?.line1 || defaultBankAddress.line1,
        line2: bankAddress?.line2 || defaultBankAddress.line2,
        locality: bankAddress?.locality || defaultBankAddress.locality,
        postalCode: bankAddress?.postalCode || defaultBankAddress.postalCode,
        region: bankAddress?.region || defaultBankAddress.region,
      };

      if (!isValid) {
        setShowBankValidatorError(true);
        if (validatedCountryField) {
          void setFieldValue(validatedCountryField, "", false);
        }
        return;
      }

      if (bankName) {
        void setFieldValue(nameField, bankName, false);
      }
      setShowBankValidatorError(false);

      if (bankAddressWithDefaults?.country && countryNameMap && validatedCountryField) {
        void setFieldValue(
          validatedCountryField,
          countryNameMap?.[bankAddressWithDefaults?.country],
        );
      }

      if (
        values &&
        values[nameField] !== bankName &&
        hasBankInfo(bankAddressWithDefaults) &&
        addressField
      ) {
        void setFieldValue(addressField, bankAddressWithDefaults, false);
      } else if (
        addressField &&
        values &&
        !hasBankInfo(values[addressField] as BankAddress | undefined) &&
        hasBankInfo(bankAddressWithDefaults)
      ) {
        void setFieldValue(addressField, bankAddressWithDefaults, false);
      }
    },
    [countryNameMap, setFieldValue, nameField, validatedCountryField, addressField, values],
  );

  return { handleClear, handleValidateCompleted };
};
